import React, { useEffect, useState, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useSelector } from "src/stateManagement/redux";
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import moment from "moment";
import { tableStyles } from 'src/shared/util/tableStyles';
import { Chart as ChartJS, LinearScale, PointElement, LineElement, Tooltip, Legend, CategoryScale, BarElement, TimeScale, LineController, BarController } from 'chart.js';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';

ChartJS.register(LinearScale, PointElement, LineElement, Tooltip, Legend, CategoryScale, BarElement, TimeScale, LineController, BarController);

interface ProductionTestProps {
  viewMode: 'table' | 'chart';
  setViewMode: (mode: 'table' | 'chart') => void;
  startDate: string;
  endDate: string;
  setStartDate: (date: string) => void;
  setEndDate: (date: string) => void;
  resetDatesRef: React.MutableRefObject<(() => void) | null>;
}

export const ProductionTest = ({ viewMode, setViewMode, startDate, endDate, setStartDate, setEndDate, resetDatesRef }: ProductionTestProps) => {
  const { productionTests } = useSelector((state) => state.location);
  const [tableData, setTableData] = useState<any[]>([]);

  useEffect(() => {
    // Ordenar por fecha descendente (más reciente primero)
    const data = productionTests
      .map((item) => ({
        ...item,
      }))
      .sort((a, b) => {
        const dateA = a.testDate ? moment(a.testDate).valueOf() : 0;
        const dateB = b.testDate ? moment(b.testDate).valueOf() : 0;
        return dateB - dateA; // Descendente
      });
    setTableData(data);
  }, [productionTests]);

  // Función para resetear fechas a min/max
  const resetDatesToMinMax = () => {
    if (tableData.length > 0) {
      const datesWithValues = tableData.filter(item => item.testDate).map(item => moment(item.testDate));
      if (datesWithValues.length > 0) {
        const minDate = moment.min(datesWithValues);
        const maxDate = moment.max(datesWithValues);
        setStartDate(minDate.format('YYYY-MM-DD'));
        setEndDate(maxDate.format('YYYY-MM-DD'));
      }
    }
  };

  // Exponer función de reset al componente padre
  useEffect(() => {
    resetDatesRef.current = resetDatesToMinMax;
    return () => {
      resetDatesRef.current = null;
    };
  }, [tableData]);

  // Inicializar fechas con min y max de los datos
  useEffect(() => {
    if (tableData.length > 0 && !startDate && !endDate) {
      resetDatesToMinMax();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData]);

  const paginationModel = { page: 0, pageSize: 10 };

  const dataColumns: GridColDef[] = [
    {
      field: "testDate",
      headerName: "Fecha de Prueba",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const formattedDate = params.value
          ? moment(params.value).format("DD-MM-YY")
          : "";
        return (
          <Typography
            variant="body2"
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
            }}
          >
            {formattedDate}
          </Typography>
        );
      },
    },
    {
      field: "testCondition",
      headerName: "Condición",
      flex: 0.8,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "testComment",
      headerName: "Comentario",
      flex: 1.5,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "grossProduction",
      headerName: "Producción Bruta",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        return (
          <Typography
            variant="body2"
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
            }}
          >
            {params.value ? parseFloat(params.value).toLocaleString() : ""}
          </Typography>
        );
      },
    },
    {
      field: "netProduction",
      headerName: "Producción Neta",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        return (
          <Typography
            variant="body2"
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
            }}
          >
            {params.value ? parseFloat(params.value).toLocaleString() : ""}
          </Typography>
        );
      },
    },
    {
      field: "potYac",
      headerName: "Pot. Yac.",
      flex: 0.8,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        return (
          <Typography
            variant="body2"
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
            }}
          >
            {params.value ? parseFloat(params.value).toLocaleString() : ""}
          </Typography>
        );
      },
    },
    {
      field: "ays",
      headerName: "AYS",
      flex: 0.8,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        return (
          <Typography
            variant="body2"
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
            }}
          >
            {params.value ? parseFloat(params.value).toLocaleString() : ""}
          </Typography>
        );
      },
    },
    {
      field: "formationGasLastTest",
      headerName: "Gas Formación",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        return (
          <Typography
            variant="body2"
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
            }}
          >
            {params.value ? parseFloat(params.value).toLocaleString() : ""}
          </Typography>
        );
      },
    },
    {
      field: "totalGasLastTest",
      headerName: "Gas Total",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        return (
          <Typography
            variant="body2"
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
            }}
          >
            {params.value ? parseFloat(params.value).toLocaleString() : ""}
          </Typography>
        );
      },
    },
  ];

  // Prepare chart data - ordenado por fecha ascendente (más antigua a la izquierda, más reciente a la derecha)
  // Aplicar filtros de fecha si están definidos
  const sortedChartData = useMemo(() => {
    let filtered = tableData.filter((item) => item.testDate);
    
    // Aplicar filtro de fecha inicio
    if (startDate) {
      const startMoment = moment(startDate).startOf('day');
      filtered = filtered.filter((item) => {
        const itemDate = moment(item.testDate);
        return itemDate.isSameOrAfter(startMoment);
      });
    }
    
    // Aplicar filtro de fecha fin
    if (endDate) {
      const endMoment = moment(endDate).endOf('day');
      filtered = filtered.filter((item) => {
        const itemDate = moment(item.testDate);
        return itemDate.isSameOrBefore(endMoment);
      });
    }
    
    // Ordenar por fecha ascendente
    return filtered.sort((a, b) => {
      const dateA = moment(a.testDate).valueOf();
      const dateB = moment(b.testDate).valueOf();
      return dateA - dateB; // Ascendente para gráfica
    });
  }, [tableData, startDate, endDate]);

  const chartData = {
    labels: sortedChartData.map((item) => moment(item.testDate).format("DD-MM-YY")),
    datasets: [
      {
        label: "Producción Bruta",
        data: sortedChartData.map((item) => 
          item.grossProduction ? parseFloat(item.grossProduction) : null
        ),
        borderColor: "rgb(128, 128, 128)", // Gris
        backgroundColor: "rgba(128, 128, 128, 0.2)",
        yAxisID: "y",
      },
      {
        label: "Producción Neta",
        data: sortedChartData.map((item) => 
          item.netProduction ? parseFloat(item.netProduction) : null
        ),
        borderColor: "rgb(76, 175, 80)", // Verde
        backgroundColor: "rgba(76, 175, 80, 0.2)",
        yAxisID: "y",
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top" as const,
      },
      tooltip: {
        mode: "index" as const,
        intersect: false,
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: "Fecha de Prueba",
        },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Producción",
        },
      },
    },
  };

  return (
    <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column"}}>
      {viewMode === 'table' ? (
        <DataGrid
          rows={tableData}
          columns={dataColumns}
          initialState={{
            pagination: { paginationModel },
          }}
          pageSizeOptions={[10, 20, 50, 100]}
          checkboxSelection={false}
          sx={{
            ...tableStyles,
            height: "100%",
          }}
          getRowId={(row) => row.id}
        />
      ) : (
        <Box sx={{ height: "calc(100vh - 300px)", p: 2 }}>
          {sortedChartData.length > 0 ? (
            <Line data={chartData} options={chartOptions} />
          ) : (
            <Typography variant="body1" sx={{ textAlign: "center", mt: 4 }}>
              No hay datos disponibles para mostrar en el rango seleccionado
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};